import React, { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, useForm } from "react-hook-form";
import { X } from "lucide-react";

import { Dialog, DialogClose, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import RHFInput from "@/components/form/RHFInput";
import { Input } from "@/components/ui/Input";
import TimezoneSelect from "@/components/ui/TimezoneSelect";
import { flattenErrors, apiErrorMessageFormat } from "@/utils/formErrorUtils";
import { to24HourDisplay, to24HourTime } from "@/utils/shiftTime";
import { shiftTimingSchema } from "../job/shift.schema";
import ShiftTimingService from "@/services/shiftTimingService";

const EMPTY_VALUES = { name: "", shiftStartTime: "", shiftEndTime: "", shiftTimezone: "" };

const toFormValues = (shift) =>
  shift
    ? {
        name: shift.name || "",
        shiftStartTime: to24HourDisplay(shift.startTime),
        shiftEndTime: to24HourDisplay(shift.endTime),
        shiftTimezone: shift.timezone || "",
      }
    : EMPTY_VALUES;

const AddEditShiftTimingModal = ({ isOpen, mode = "add", shift, onClose, onSaved }) => {
  const isEdit = mode === "edit";
  const [submitting, setSubmitting] = useState(false);
  const [inlineError, setInlineError] = useState("");

  const methods = useForm({
    resolver: zodResolver(shiftTimingSchema),
    defaultValues: toFormValues(shift),
    mode: "onSubmit",
  });

  const {
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = methods;

  useEffect(() => {
    if (isOpen) {
      reset(toFormValues(shift));
      setInlineError("");
    }
  }, [isOpen, shift, reset]);

  const flattened = flattenErrors(errors);
  const shiftStartTime = watch("shiftStartTime");
  const shiftEndTime = watch("shiftEndTime");
  const shiftTimezone = watch("shiftTimezone");

  const submitForm = async (values) => {
    const payload = {
      name: values.name.trim(),
      startTime: to24HourTime(values.shiftStartTime),
      endTime: to24HourTime(values.shiftEndTime),
      timezone: values.shiftTimezone,
    };

    try {
      setSubmitting(true);
      setInlineError("");
      if (isEdit) {
        await ShiftTimingService.update(shift.id, payload);
      } else {
        await ShiftTimingService.create(payload);
      }
      onSaved?.();
    } catch (err) {
      setInlineError(apiErrorMessageFormat(err, "Failed to save shift."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="flex w-[95vw] max-w-[520px] max-h-[90vh] flex-col overflow-hidden p-0">
        <DialogHeader className="relative shrink-0 border-b border-border-primary px-5 py-3">
          <div className="pr-10">
            <DialogTitle className="text-[15px] font-semibold text-text-primary">{isEdit ? "Edit Shift" : "Add Shift"}</DialogTitle>
            <DialogDescription className="mt-0.5 text-[12px] text-text-primary/70">
              Define a shift name, start/end time and time zone. Jobs created after this will be able to select it.
            </DialogDescription>
          </div>
          <DialogClose className="absolute right-5 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full bg-background-icon/40">
            <X size={16} strokeWidth={3} />
          </DialogClose>
        </DialogHeader>

        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(submitForm)} className="flex min-h-0 flex-1 flex-col">
            <div className="custom-scrollbar flex-1 min-h-0 space-y-4 overflow-y-auto p-5">
              <RHFInput name="name" label="Shift Name" placeholder="e.g. General Shift" required />

              <div className="flex flex-col sm:flex-row gap-2" data-field="shiftStartTime">
                <div className="flex-1">
                  <label htmlFor="shiftStartTime" className="block text-xs mb-1 text-text-muted">
                    Start Time <span className="text-red-500 font-bold">*</span>
                  </label>
                  <Input
                    id="shiftStartTime"
                    type="time"
                    value={shiftStartTime}
                    onChange={(e) => setValue("shiftStartTime", e.target.value, { shouldValidate: true, shouldDirty: true })}
                    className={flattened.shiftStartTime ? "border-red-500" : ""}
                  />
                  {flattened.shiftStartTime && <p className="text-red-400 text-xs mt-1">{flattened.shiftStartTime.message}</p>}
                </div>
                <div className="flex-1" data-field="shiftEndTime">
                  <label htmlFor="shiftEndTime" className="block text-xs mb-1 text-text-muted">
                    End Time <span className="text-red-500 font-bold">*</span>
                  </label>
                  <Input
                    id="shiftEndTime"
                    type="time"
                    value={shiftEndTime}
                    onChange={(e) => setValue("shiftEndTime", e.target.value, { shouldValidate: true, shouldDirty: true })}
                    className={flattened.shiftEndTime ? "border-red-500" : ""}
                  />
                  {flattened.shiftEndTime && <p className="text-red-400 text-xs mt-1">{flattened.shiftEndTime.message}</p>}
                </div>
              </div>

              <div data-field="shiftTimezone">
                {/* TimezoneSelect prints `error` itself — a sibling <p> here
                    showed the same sentence twice. */}
                <TimezoneSelect
                  value={shiftTimezone}
                  onChange={(val) => setValue("shiftTimezone", val, { shouldValidate: true, shouldDirty: true })}
                  label={
                    <span>
                      Time Zone <span className="text-red-500 font-bold">*</span>
                    </span>
                  }
                  error={flattened.shiftTimezone?.message}
                />
              </div>

              {inlineError ? (
                <div className="rounded-xl border border-[rgb(var(--color-error))]/20 bg-[rgb(var(--color-error))]/10 px-3 py-2 text-xs font-medium text-[rgb(var(--color-error))]">
                  {inlineError}
                </div>
              ) : null}
            </div>

            <div className="shrink-0 border-t border-border-primary bg-background-secondary px-5 py-4">
              <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <Button type="button" onClick={onClose} className="w-full sm:w-auto rounded-full px-4 py-2 bg-background-icon text-text-primary">
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submitting || isSubmitting}
                  className="w-full sm:w-auto rounded-full px-4 py-2 bg-button-secondary text-text-base disabled:opacity-60"
                >
                  {submitting ? (isEdit ? "Updating..." : "Creating...") : isEdit ? "Update Shift" : "Create Shift"}
                </Button>
              </div>
            </div>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
};

export default AddEditShiftTimingModal;
